import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../contexts/BookingContext';
import ProgressBar from '../components/ProgressBar';

const CultureExperiencePage: React.FC = () => {
  const navigate = useNavigate();
  const { addCultureExperience } = useBooking();
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);

  // 根據季節推薦文化體驗
  const getSeasonalRecommendations = () => {
    const currentMonth = new Date().getMonth();
    const season = 
      currentMonth >= 2 && currentMonth <= 5 ? 'spring' :
      currentMonth >= 6 && currentMonth <= 8 ? 'summer' :
      currentMonth >= 9 && currentMonth <= 11 ? 'autumn' : 'winter';
    
    return cultureOptions.filter(option => option.season === season || option.season === 'all');
  };

  const cultureOptions = [
    {
      id: 1,
      name: '阿美族豐年祭體驗',
      description: '參與原住民傳統祭典，體驗阿美族文化精髓，學習傳統歌舞與手工藝。',
      icon: '🎭',
      type: '原住民文化',
      duration: '3-4小時',
      location: '花蓮市區',
      price: 'NT$ 800',
      season: 'summer',
      difficulty: '簡單',
      groupSize: '8-15人',
      language: '中文/阿美族語',
      features: ['傳統歌舞', '手工藝製作', '文化解說', '拍照留念']
    },
    {
      id: 2,
      name: '太魯閣族織布工藝',
      description: '學習太魯閣族傳統織布技術，製作獨特的手工織品，了解織布文化意義。',
      icon: '🧵',
      type: '手工藝',
      duration: '2-3小時',
      location: '秀林鄉',
      price: 'NT$ 600',
      season: 'all',
      difficulty: '中等',
      groupSize: '4-8人',
      language: '中文',
      features: ['織布教學', '材料提供', '作品帶回', '文化故事']
    },
    {
      id: 3,
      name: '花蓮客家擂茶體驗',
      description: '體驗客家傳統擂茶文化，學習製作擂茶，品嚐客家特色茶點。',
      icon: '🍵',
      type: '飲食文化',
      duration: '1.5-2小時',
      location: '吉安鄉',
      price: 'NT$ 400',
      season: 'all',
      difficulty: '簡單',
      groupSize: '6-12人',
      language: '中文/客語',
      features: ['擂茶製作', '茶點品嚐', '文化解說', '茶具展示']
    },
    {
      id: 4,
      name: '春季賞櫻文化之旅',
      description: '在櫻花盛開的季節，結合賞櫻與日本文化體驗，感受東洋美學。',
      icon: '🌸',
      type: '季節活動',
      duration: '4-5小時',
      location: '吉安鄉',
      price: 'NT$ 1,200',
      season: 'spring',
      difficulty: '簡單',
      groupSize: '10-20人',
      language: '中文/日文',
      features: ['賞櫻導覽', '和服體驗', '茶道體驗', '攝影指導']
    },
    {
      id: 5,
      name: '夏季海洋文化探索',
      description: '探索花蓮海洋文化，學習傳統漁法，體驗海鮮料理製作。',
      icon: '🌊',
      type: '海洋文化',
      duration: '3-4小時',
      location: '豐濱鄉',
      price: 'NT$ 900',
      season: 'summer',
      difficulty: '中等',
      groupSize: '6-12人',
      language: '中文',
      features: ['漁法體驗', '海鮮料理', '海洋生態', '文化解說']
    },
    {
      id: 6,
      name: '秋季豐收祭典體驗',
      description: '參與秋季豐收祭典，體驗農耕文化，學習傳統農具使用。',
      icon: '🌾',
      type: '農耕文化',
      duration: '2-3小時',
      location: '玉里鎮',
      price: 'NT$ 500',
      season: 'autumn',
      difficulty: '簡單',
      groupSize: '8-15人',
      language: '中文',
      features: ['農具體驗', '豐收儀式', '農產品品嚐', '文化解說']
    },
    {
      id: 7,
      name: '冬季溫泉文化體驗',
      description: '體驗花蓮溫泉文化，結合原住民溫泉療法與現代養生概念。',
      icon: '♨️',
      type: '養生文化',
      duration: '2-3小時',
      location: '瑞穗鄉',
      price: 'NT$ 1,500',
      season: 'winter',
      difficulty: '簡單',
      groupSize: '4-10人',
      language: '中文',
      features: ['溫泉體驗', '養生解說', '按摩服務', '養生茶飲']
    },
    {
      id: 8,
      name: '花蓮石藝文化之旅',
      description: '探索花蓮玉石文化，學習玉石鑑賞，體驗玉石雕刻工藝。',
      icon: '💎',
      type: '工藝文化',
      duration: '3-4小時',
      location: '花蓮市區',
      price: 'NT$ 1,000',
      season: 'all',
      difficulty: '中等',
      groupSize: '6-10人',
      language: '中文',
      features: ['玉石鑑賞', '雕刻體驗', '作品帶回', '文化解說']
    }
  ];

  const seasonalRecommendations = getSeasonalRecommendations();

  const handleOptionToggle = (option: any) => {
    const isSelected = selectedOptions.find(item => item.id === option.id);
    if (isSelected) {
      setSelectedOptions(selectedOptions.filter(item => item.id !== option.id));
    } else {
      setSelectedOptions([...selectedOptions, option]);
    }
  };

  const handleNext = () => {
    selectedOptions.forEach(option => {
      addCultureExperience(option);
    });
    navigate('/summary');
  };

  const handleSkip = () => {
    navigate('/summary');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <ProgressBar currentStep={6} />
        
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            文化體驗選擇
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            選擇您感興趣的文化體驗，深入了解花蓮的多元文化魅力
          </p>
        </div>

        {/* 季節推薦 */}
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-6 mb-8 border border-orange-200">
          <h2 className="text-2xl font-semibold text-orange-800 mb-4 flex items-center">
            🌸 本季推薦體驗
          </h2>
          <p className="text-orange-700 mb-4">
            根據當前季節，我們為您精選了最適合的文化體驗活動
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {seasonalRecommendations.slice(0, 3).map((option) => (
              <div key={option.id} className="bg-white rounded-xl p-4 border border-orange-200">
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">{option.icon}</span>
                  <span className="font-semibold text-orange-800">{option.name}</span>
                </div>
                <p className="text-sm text-orange-700">{option.description}</p>
                <div className="mt-2 text-sm text-orange-600">
                  {option.price} • {option.duration}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 所有文化體驗選項 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {cultureOptions.map((option) => {
            const isSelected = selectedOptions.find(item => item.id === option.id);
            const isRecommended = seasonalRecommendations.find(item => item.id === option.id);
            
            return (
              <div
                key={option.id}
                onClick={() => handleOptionToggle(option)}
                className={`bg-white rounded-2xl shadow-soft p-6 cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-medium relative ${
                  isSelected
                    ? 'ring-4 ring-orange-200 bg-orange-50'
                    : 'hover:bg-gray-50'
                }`}
              >
                {/* 推薦標籤 */}
                {isRecommended && (
                  <div className="absolute top-4 left-4 z-10">
                    <span className="bg-gradient-to-r from-orange-400 to-yellow-400 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-medium">
                      🌸 本季推薦
                    </span>
                  </div>
                )}

                <div className="text-4xl mb-4">{option.icon}</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {option.name}
                </h3>
                <p className="text-gray-600 mb-4">{option.description}</p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="mr-2">🏷️</span>
                    <span>{option.type}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="mr-2">⏰</span>
                    <span>{option.duration}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="mr-2">📍</span>
                    <span>{option.location}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="mr-2">👥</span>
                    <span>{option.groupSize}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 text-sm rounded-full font-semibold">
                      {option.price}
                    </span>
                  </div>
                  
                  {isSelected && (
                    <div className="text-orange-600 font-semibold">
                      <span className="mr-1">✓</span>
                      已選擇
                    </div>
                  )}
                </div>

                {/* 特色功能 */}
                <div className="mt-4 flex flex-wrap gap-1">
                  {option.features.slice(0, 3).map((feature, index) => (
                    <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* 導航按鈕 */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {selectedOptions.length > 0 && (
            <button
              onClick={handleNext}
              className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-3 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-medium"
            >
              下一步 ({selectedOptions.length} 個體驗)
            </button>
          )}
          
          <button
            onClick={handleSkip}
            className="w-full sm:w-auto bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-8 rounded-2xl transition-all duration-300"
          >
            略過文化體驗
          </button>
        </div>

        {selectedOptions.length > 0 && (
          <div className="mt-8 bg-orange-50 rounded-2xl p-6 max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold text-orange-800 mb-2">
              已選擇 {selectedOptions.length} 個文化體驗：
            </h3>
            <div className="space-y-1">
              {selectedOptions.map((option) => (
                <p key={option.id} className="text-orange-600">
                  • {option.name} ({option.price})
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CultureExperiencePage; 